import axios, { InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { isTokenValid } from 'contexts/JWTContext';
import { getItemLocalStorage } from 'hooks/useLocalStorage';
import { showErrorToast } from './showErrorToast';
import { RoutesEnum } from './routesEnum';

const axiosServices = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3010/' });
const accessToken = getItemLocalStorage('accessToken');

// Cache para armazenar requisições pendentes
export const pendingRequests = new Map<string, AbortController>();

// Enum para rotas que devem ter cancelamento global
export enum CancelOnDuplicateRoutes {
  CUSTOMERS = '/api/contact/v1/customers',
  REPORT_ATTENDANCES_REASONS = '/api/report-attendance/v1/total-finished-reasons',
  REPORT_STATUS_ATTENDANCES = '/api/report-attendance/v1/total-status-attendances',
  REPORT_ATTENDANCES = '/api/report-attendance/v1/attendances'
}

// Função utilitária para verificar se a rota está no enum
function isCancelOnDuplicateRoute(url?: string): boolean {
  if (!url) return false;
  let baseUrl = url.split('?')[0];
  try {
    // Extrai apenas o path da URL, mesmo que venha completa
    const urlObj = new URL(baseUrl, window.location.origin);
    baseUrl = urlObj.pathname;
  } catch {
    // Se já for só o path, mantém
  }
  if (baseUrl.endsWith('/')) baseUrl = baseUrl.slice(0, -1);
  return Object.values(CancelOnDuplicateRoutes).includes(baseUrl as CancelOnDuplicateRoutes);
}

// Função para gerar uma chave única para cada requisição
const generateRequestKey = (config: InternalAxiosRequestConfig): string => {
  const { url, method, params, data } = config;
  const baseUrl = url?.split('?')[0];
  // Se a rota estiver no enum, use apenas a URL base e o método
  if (isCancelOnDuplicateRoute(url)) {
    return [baseUrl, method].join('&');
  }
  // Para os demais, mantenha o comportamento original
  return [url, method, JSON.stringify(params), JSON.stringify(data)].join('&');
};

// ==============================|| AXIOS - FOR MOCK SERVICES ||============================== //

axiosServices.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Gera uma chave única para a requisição
    const requestKey = generateRequestKey(config);

    // Se já existe uma requisição pendente com a mesma chave, cancela ela
    if (pendingRequests.has(requestKey)) {
      const controller = pendingRequests.get(requestKey);
      controller?.abort();
    }

    // Cria um novo controller para esta requisição
    const controller = new AbortController();
    config.signal = controller.signal;

    // Armazena o controller no cache
    pendingRequests.set(requestKey, controller);

    // Remove o controller do cache quando a requisição for concluída
    (config as any).metadata = { requestKey };

    if (isTokenValid(accessToken)) {
      config.headers['X-Access-Token'] = accessToken;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosServices.interceptors.response.use(
  (response: AxiosResponse) => {
    // Remove a requisição do cache quando concluída com sucesso
    const requestKey = (response.config as any).metadata?.requestKey;
    if (requestKey) {
      pendingRequests.delete(requestKey);
    }
    return response;
  },
  (error) => {
    // Remove a requisição do cache quando ocorre um erro
    if ((error.config as any)?.metadata?.requestKey) {
      pendingRequests.delete((error.config as any).metadata.requestKey);
    }

    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    if (error.response && error.response.status === 502) {
      window.location.replace('/maintenance/error/500');
    } else if (
      error.response?.status === 401 &&
      !window.location.href.includes(RoutesEnum.LOGIN) &&
      !error.config.baseURL.includes('mock-data-api')
    ) {
      showErrorToast('Sessão expirada.');
      window.location.href = RoutesEnum.LOGIN;
    } else if (error.response && [400, 403, 409].includes(error.response.status)) {
      // 403/409 (regra de negócio: motivo de sistema, perfil em uso...) também trazem `description`
      if (error.response.data?.description) {
        showErrorToast(error.response.data.description);
      }
    }
    return Promise.reject((error.response && error.response.data) || 'Wrong Services');
  }
);

export default axiosServices;
